import { Node as ReactFlowNode } from 'reactflow';
import useAccount from '../../../hooks/useAccount';
import type { UrlFilters } from '../../../hooks/useUrlFilters';
import type { Report } from '../../../types/types';
import { isInSaaSMode } from '../../../utils/common';
import type {
  ApiLineage,
  HopLimit,
  LineageDataResult,
  LineageGraphSource,
  ReactFlowNodeData,
  StreamDirection,
} from '../types';
import useLineageGraph from './useLineageGraph';
import useLineageGraphSource from './useLineageGraphSource';
import useLineageWorkerData from './useLineageWorkerData';

type Props = {
  reportData?: Report;
  graphNodes?: ReactFlowNode<ReactFlowNodeData, string | undefined>[];
  assetIds?: string[];
  columnIds?: string[];
  filters?: UrlFilters;
  streamDirection?: StreamDirection;
  hopLimit?: HopLimit;
  calcDownstreamUpstream?: boolean;
  forceLoadLargeGraph?: boolean;
  /** Pre-fetched lineage payload used instead of the GraphQL fetch; still requires `assetIds`/`columnIds` for root traversal. */
  overrideLineageData?: ApiLineage | null;
  /** Backend render graph source used instead of the lineage page query. */
  overrideGraphSource?: LineageGraphSource;
};

const useLineageData = (props: Props): LineageDataResult => {
  const { data: account, isFetched: isAccountFetched } = useAccount();
  const isSaaS = isInSaaSMode();
  const lineagePageGraphSource = useLineageGraphSource({
    assetIds: props.assetIds,
    columnIds: props.columnIds,
    filters: props.filters,
    hopLimit: props.hopLimit,
  });

  const isBackendGraphEnabled =
    isSaaS &&
    !!account?.lineage_backend_graph_enabled &&
    props.overrideLineageData == null;
  const graphSource =
    props.overrideGraphSource ??
    (isBackendGraphEnabled ? lineagePageGraphSource : undefined);
  const isBackendGraph =
    props.overrideGraphSource != null || isBackendGraphEnabled;
  const isGraphModeResolved =
    !isSaaS || isAccountFetched || props.overrideGraphSource != null;

  const workerLineageData = useLineageWorkerData({
    ...props,
    enabled: isGraphModeResolved && !isBackendGraph,
  });
  const backendLineageData = useLineageGraph({
    enabled: isGraphModeResolved && isBackendGraph,
    source: graphSource,
    graphNodes: props.graphNodes,
    assetIds: props.assetIds,
    columnIds: props.columnIds,
    filters: props.filters,
    hopLimit: props.hopLimit,
    forceLoadLargeGraph: props.forceLoadLargeGraph,
  });

  return isBackendGraph ? backendLineageData : workerLineageData;
};

export default useLineageData;
